import * as i6 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, ElementRef, Component, Input, HostBinding, ViewChild, ViewChildren, HostListener, EventEmitter, Output, forwardRef, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { NgControl, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { DateTime, Info } from 'luxon';
import { __awaiter } from 'tslib';
import * as i7 from '@angular/cdk/a11y';
import { FocusKeyManager, A11yModule } from '@angular/cdk/a11y';
import { Subject } from 'rxjs';
import * as i2$1 from 'se-fe-form-field-suffix';
import { SeFeFormFieldSuffixModule } from 'se-fe-form-field-suffix';
import * as i1 from 'se-fe-button';
import { SeFeButtonModule } from 'se-fe-button';

const DATE_FORMAT = 'MM/dd/yyyy';
var SeFeCalendarSvgPath;
(function (SeFeCalendarSvgPath) {
    SeFeCalendarSvgPath["range"] = "M19 4h-1V3c0-.55-.45-1-1-1s-1 .45-1 1v1H8V3c0-.55-.45-1-1-1s-1 .45-1 1v1H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 15c0 .55-.45 1-1 1H6c-.55 0-1-.45-1-1V9h14v10zM7 11h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z";
    SeFeCalendarSvgPath["today"] = "M19 3h-1V2c0-.55-.45-1-1-1s-1 .45-1 1v1H8V2c0-.55-.45-1-1-1s-1 .45-1 1v1H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 16H6c-.55 0-1-.45-1-1V8h14v10c0 .55-.45 1-1 1zM8 10h3c.55 0 1 .45 1 1v3c0 .55-.45 1-1 1H8c-.55 0-1-.45-1-1v-3c0-.55.45-1 1-1z";
})(SeFeCalendarSvgPath || (SeFeCalendarSvgPath = {}));

function excludedDate(date, exclusions) {
    date = date || '';
    const luxDate = DateTime.fromFormat(date, DATE_FORMAT);
    if (!luxDate.isValid)
        return false;
    const isoDate = luxDate.toISODate();
    for (const ex of exclusions) {
        if (!ex.start && isoDate <= ex.end) {
            return true;
        }
        if (!ex.end && isoDate >= ex.start)
            return true;
        if (isoDate >= ex.start && isoDate <= ex.end)
            return true;
    }
    return false;
}
function dateExcludedValidator(exclusions) {
    return (control) => {
        return excludedDate(control.value, exclusions) ? { dateOutOfRange: true } : null;
    };
}

function validYear(date, years) {
    date = date || '';
    const luxDate = DateTime.fromFormat(date, DATE_FORMAT);
    const currentYear = new Date().getFullYear();
    const maxYear = currentYear + (years.future || 0);
    const minYear = currentYear - (years.past || 0);
    if (!luxDate.isValid)
        return false;
    const controlYear = luxDate.year;
    if (controlYear < minYear && controlYear < maxYear)
        return true;
    return false;
}
function dateYearRangeValidator(years) {
    return (control) => {
        return validYear(control.value, years) ? { dateOutOfRange: true } : null;
    };
}

class SeFeDatepickerService {
    constructor() {
        this.date = DateTime.local();
        this.excludedDate = excludedDate;
        this.calendarDays$ = new Subject();
        this.selectedDate$ = new Subject();
    }
    selectedDate(date, exclude) {
        if (this.excludedDate(date.toFormat(DATE_FORMAT), exclude))
            return;
        this.inputDate = this.date = date;
        this.selectedDate$.next(date);
    }
    createCalendar() {
        const firstDay = this.date.startOf('month');
        const { daysInMonth } = this.date;
        const days = Array.from({ length: daysInMonth })
            .map((_, i) => i)
            .map((n) => {
            return firstDay.plus({ days: n });
        });
        const previousMonth = this.date.minus({ month: 1 });
        const startOfWeek = firstDay.weekday === 7 ? 0 : firstDay.weekday;
        for (let n = 0; n < startOfWeek; n++) {
            days.unshift(previousMonth.endOf('month').plus({ days: -n }));
        }
        const nextMonth = this.date.plus({ month: 1 });
        for (let n = 0; n < this.remainingCalendarDays(this.date); n++) {
            days.push(nextMonth.startOf('month').plus({ days: n }));
        }
        this.calendarDays$.next(days);
    }
    remainingCalendarDays(date) {
        const amountofDay = date.daysInMonth + date.startOf('month').weekday;
        const weeksToDisplay = Math.ceil((date.daysInMonth + date.startOf('month').weekday) / 7);
        return weeksToDisplay * 7 - amountofDay;
    }
}
SeFeDatepickerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SeFeDatepickerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

class SeFeDatepickerCalendarDayComponent {
    constructor(service) {
        this.service = service;
        this.excludedDate = excludedDate;
        this.validYear = validYear;
        this.exclude = [];
        this.tabindex = false;
        this.role = 'cell';
    }
    focus() {
        this.dayEl.nativeElement.focus();
        this.tabindex = true;
    }
    selectedDate(date) {
        this.service.selectedDate(date, this.exclude);
    }
    isSelected(date) {
        if (!this.service.inputDate)
            return;
        return date.equals(this.service.inputDate);
    }
    index(i) {
        return i;
    }
    onKeyup(event, day) {
        if (event.code === 'Enter' || event.code === 'Space') {
            event.stopPropagation();
            event.preventDefault();
            this.selectedDate(day);
        }
    }
    isToday(date) {
        return date ?
            DateTime.now().toFormat(DATE_FORMAT) === date.toFormat(DATE_FORMAT) :
            false;
    }
    isDifferentMonth(date) {
        const displayedMonth = this.service.date.month;
        return date ?
            displayedMonth !== date.month :
            false;
    }
    disableDate(day) {
        const date = day.toFormat('MM/dd/yyyy');
        return this.excludedDate(date, this.exclude) || this.validYear(date, this.yearRange);
    }
    ngOnInit() {
        this.service.createCalendar();
    }
    get ariaLabelDay() {
        return `${this.day.toFormat('cccc LLLL d yyyy')}`;
    }
}
SeFeDatepickerCalendarDayComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarDayComponent, deps: [{ token: SeFeDatepickerService }], target: i0.ɵɵFactoryTarget.Component });
SeFeDatepickerCalendarDayComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeDatepickerCalendarDayComponent, selector: "se-fe-datepicker-calendar-day", inputs: { exclude: ["seFeDataExclude", "exclude"], yearRange: ["seFeDataYearRange", "yearRange"], day: ["seFeDataDay", "day"], tabindex: "tabindex" }, host: { properties: { "attr.role": "this.role" } }, viewQueries: [{ propertyName: "dayEl", first: true, predicate: ["datepickerDay"], descendants: true, read: ElementRef }], ngImport: i0, template: "\n<div\n  #datepickerDay\n  class=\"se-fe-datepicker__day\"\n  role=\"button\"\n  [ngClass]=\"{\n    'se-fe-datepicker__day--different-month': isDifferentMonth(day),\n    'se-fe-datepicker__day--today': isToday(day),\n    'se-fe-datepicker__day--selected': isSelected(day)\n  }\"\n  [attr.tabindex]=\"tabindex ? '0' : '-1'\"\n  [attr.aria-selected]=\"isSelected(day)\"\n  [attr.aria-disabled]=\"disableDate(day)\"\n  [attr.aria-label]=\"ariaLabelDay\"\n  (click)=\"selectedDate(day)\"\n  (keypress)=\"onKeyup($event, day)\">\n    {{ day.toFormat('d') }}\n</div>\n", styles: [".se-fe-datepicker__day{font-size:14px;display:flex;width:40px;height:40px;justify-content:center;align-items:center;cursor:default;border:1px solid transparent;border-radius:50%;box-sizing:border-box;padding:2px}.se-fe-datepicker__day[tabindex=\"0\"]{background:rgba(0,102,143,.08)}.se-fe-datepicker__day--today{border-color:#68767f}.se-fe-datepicker__day--selected{background:rgba(0,102,143,.16)}.se-fe-datepicker__day:focus{outline:1px solid #fff;outline-offset:-.5px;box-shadow:0 0 0 calc(0px + 2px) #fff,0 0 0 calc(0px + 4px) #00668f,0 0 0 calc(0px + 6px) #fff}.se-fe-datepicker__day:focus[tabindex=\"0\"]{background:unset}.se-fe-datepicker__day:hover{background:rgba(0,102,143,.04)}.se-fe-datepicker__day[aria-disabled=true]{display:flex;color:#90a0a7;justify-content:center;align-items:center;cursor:not-allowed;background:none}.se-fe-datepicker__day--different-month{color:#d0d7db!important}.se-fe-datepicker__day--different-month.se-fe-datepicker__day--selected{background:unset}.se-fe-datepicker__day--different-month.se-fe-datepicker__day--selected:focus{outline:0;box-shadow:none;background:none}\n"], directives: [{ type: i6.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarDayComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-datepicker-calendar-day',
                    templateUrl: './se-fe-datepicker-calendar-day.component.html',
                    styleUrls: ['./se-fe-datepicker-calendar-day.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: SeFeDatepickerService }]; }, propDecorators: { exclude: [{
                type: Input,
                args: ['seFeDataExclude']
            }], yearRange: [{
                type: Input,
                args: ['seFeDataYearRange']
            }], day: [{
                type: Input,
                args: ['seFeDataDay']
            }], tabindex: [{
                type: Input
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], dayEl: [{
                type: ViewChild,
                args: ['datepickerDay', { read: ElementRef, static: false }]
            }] } });

class SeFeDatepickerCalendarComponent {
    constructor(service, change) {
        this.service = service;
        this.change = change;
        this.weekdays = [];
        this.days = [];
        this.weeks = [];
        this.exclude = [];
        this.buildWeekdays();
        this.subscription = [
            this.service.calendarDays$.subscribe(days => {
                if (this.keyManager) {
                    setTimeout(() => {
                        this.ngAfterViewInit();
                    });
                }
                this.days = days;
                const weekSize = 7;
                this.weeks = [];
                for (let i = 0; i < this.days.length; i += weekSize) {
                    const chunk = this.days.slice(i, i + weekSize);
                    this.weeks.push(chunk);
                }
            }),
        ];
    }
    manage(event) {
        if (!this.keyManager.activeItem) {
            this.setInitalActiveItem();
            return;
        }
        const lastActiveDay = this.keyManager.activeItem.day;
        const lastActiveItem = this.keyManager.activeItem;
        const formattedLastActiveDay = new Date(`${lastActiveDay.month}/${lastActiveDay.day}/${lastActiveDay.year}`);
        if (event.shiftKey && event.code === 'PageUp') {
            this.service.date = this.service.date.plus({ month: 1 });
            this.service.createCalendar();
        }
        if (event.shiftKey && event.code === 'PageDown') {
            this.service.date = this.service.date.minus({ month: 1 });
            this.service.createCalendar();
        }
        switch (event.code) {
            case 'ArrowDown':
                event.preventDefault();
                lastActiveItem.tabindex = false;
                const downArrowIndex = this.keyManager.activeItemIndex + 7;
                const downArrowNewDay = lastActiveDay.day + 7;
                // next month
                if (downArrowNewDay > lastActiveDay.daysInMonth) {
                    const downNewDay = lastActiveDay.day + 7;
                    this.updateCalendar(formattedLastActiveDay, downNewDay);
                    break;
                }
                this.keyManager.setActiveItem(downArrowIndex);
                this.service.date = this.keyManager.activeItem.day;
                break;
            case 'ArrowUp':
                event.preventDefault();
                lastActiveItem.tabindex = false;
                const upArrowIndex = this.keyManager.activeItemIndex - 7;
                const upArrowNewDay = lastActiveDay.day - 7;
                // previous month
                if (upArrowNewDay <= 0) {
                    const upNewDay = lastActiveDay.day - 7;
                    this.updateCalendar(formattedLastActiveDay, upNewDay);
                    break;
                }
                this.keyManager.setActiveItem(upArrowIndex);
                this.service.date = this.keyManager.activeItem.day;
                break;
            case 'ArrowRight':
                event.preventDefault();
                lastActiveItem.tabindex = false;
                const rightArrowNewDay = lastActiveDay.day + 1;
                if (rightArrowNewDay > lastActiveDay.daysInMonth) {
                    const rightNewDay = lastActiveDay.day + 1;
                    this.updateCalendar(formattedLastActiveDay, rightNewDay);
                    break;
                }
                this.keyManager.setNextItemActive();
                this.service.date = this.keyManager.activeItem.day;
                break;
            case 'ArrowLeft':
                event.preventDefault();
                lastActiveItem.tabindex = false;
                const leftArrowNewDay = lastActiveDay.day - 1;
                if (leftArrowNewDay <= 0) {
                    const leftNewDay = lastActiveDay.day - 1;
                    this.updateCalendar(formattedLastActiveDay, leftNewDay);
                    break;
                }
                this.keyManager.setPreviousItemActive();
                this.service.date = this.keyManager.activeItem.day;
                break;
            case 'Home':
                event.preventDefault();
                if (lastActiveDay.weekday === 7) {
                    break;
                }
                const homeNewDay = lastActiveDay.day - lastActiveDay.weekday;
                this.updateCalendar(formattedLastActiveDay, homeNewDay);
                lastActiveItem.tabindex = false;
                break;
            case 'End':
                event.preventDefault();
                if (lastActiveDay.weekday === 6) {
                    break;
                }
                const dayAdjust = lastActiveDay.weekday === 7 ? 6 : 6 - lastActiveDay.weekday;
                const endNewDay = lastActiveDay.day + dayAdjust;
                this.updateCalendar(formattedLastActiveDay, endNewDay);
                lastActiveItem.tabindex = false;
                break;
            case 'PageUp':
                event.preventDefault();
                if (event.shiftKey)
                    break;
                this.service.date = this.service.date.minus({ month: 1 });
                this.service.createCalendar();
                break;
            case "PageDown":
                event.preventDefault();
                if (event.shiftKey)
                    break;
                this.service.date = this.service.date.plus({ month: 1 });
                this.service.createCalendar();
                break;
            default:
                break;
        }
    }
    updateCalendar(date, day) {
        return __awaiter(this, void 0, void 0, function* () {
            this.service.date = DateTime.fromJSDate(new Date(date.setDate(day)));
            yield this.service.createCalendar();
            this.change.detectChanges();
            this.keyManager.setActiveItem(this.newDateIndex);
        });
    }
    setInitalActiveItem() {
        if (this.keyManager.activeItem)
            return;
        setTimeout(() => {
            this.keyManager.setActiveItem(this.newDateIndex);
        });
    }
    ngOnDestroy() {
        this.subscription.forEach(sub => sub.unsubscribe());
    }
    ngOnInit() {
        this.service.createCalendar();
    }
    ngAfterViewInit() {
        this.keyManager = new FocusKeyManager(this.daysList);
        this.tabindex();
        this.setInitalActiveItem();
    }
    buildWeekdays() {
        const weekdayNum = [0, 1, 2, 3, 4, 5, 6];
        weekdayNum.forEach(d => {
            const luxonDay = (d + 6) % 7;
            this.weekdays.push({ long: Info.weekdays('long')[luxonDay], short: Info.weekdays('short')[luxonDay].slice(0, 2) });
        });
    }
    get newDateIndex() {
        return this.daysList.toArray().findIndex(d => {
            return d.day.day === this.service.date.day && d.day.month === this.service.date.month;
        });
    }
    index(i, day) {
        return day.month;
    }
    tabindex() {
        const addTabIndexEl = this.daysList.find(d => d.day.day === this.service.date.day && d.day.month === this.service.date.month);
        addTabIndexEl ? addTabIndexEl.tabindex = true : '';
        this.daysList.forEach(d => {
            if (d.tabindex === true && d.day.day !== this.service.date.day && d.day.month !== this.service.date.month) {
                d.tabindex = false;
            }
        });
    }
}
SeFeDatepickerCalendarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarComponent, deps: [{ token: SeFeDatepickerService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
SeFeDatepickerCalendarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeDatepickerCalendarComponent, selector: "se-fe-datepicker-calendar", inputs: { exclude: ["seFeDataExclude", "exclude"], yearRange: ["seFeDataYearRange", "yearRange"], id: ["seFeDataTableId", "id"] }, host: { listeners: { "keydown": "manage($event)" } }, viewQueries: [{ propertyName: "daysList", predicate: SeFeDatepickerCalendarDayComponent, descendants: true }], ngImport: i0, template: "\n  <table role=\"grid\" class=\"se-fe-datepicker__table\" tabindex=\"2\">\n    <thead>\n      <tr class=\"se-fe-datepicker__table-row\">\n        <th *ngFor=\"let weekday of weekdays\" scope=\"col\" class=\"se-fe-datepicker__weekday\" [attr.abbr]=\"weekday.long\">\n          {{ weekday.short }}\n        </th>\n      </tr>\n    </thead>\n    <tbody>\n      <tr class=\"se-fe-datepicker__table-row\" *ngFor=\"let week of weeks; let i = index; trackBy: index\">\n          <se-fe-datepicker-calendar-day\n          *ngFor=\"let day of week; let i = index; trackBy: index\"\n          [seFeDataDay]=\"day\"\n          [seFeDataExclude]=\"exclude\"\n          [seFeDataYearRange]=\"yearRange\">\n        </se-fe-datepicker-calendar-day>\n      </tr>\n    </tbody>\n  </table>\n", styles: [".se-fe-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0}.se-fe-sr-only-focusable:focus{position:relative;width:auto;height:auto;clip:initial;margin:inherit;padding:inherit;border:initial}.se-fe-datepicker__table{display:flex;flex-direction:column;flex-wrap:wrap;justify-content:space-around}.se-fe-datepicker__table-row{display:flex;flex-direction:row;margin-bottom:4px;justify-content:space-around;padding:0 3px}.se-fe-datepicker__table-row .se-fe-datepicker__weekday{display:flex;width:44px;height:32px;font-size:calc(var(--se-rem, 1rem) * .8571428571);justify-content:center;align-items:flex-end;font-weight:600}\n"], components: [{ type: SeFeDatepickerCalendarDayComponent, selector: "se-fe-datepicker-calendar-day", inputs: ["seFeDataExclude", "seFeDataYearRange", "seFeDataDay", "tabindex"] }], directives: [{ type: i6.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-datepicker-calendar',
                    templateUrl: './se-fe-datepicker-calendar.component.html',
                    styleUrls: ['./se-fe-datepicker-calendar.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: SeFeDatepickerService }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { exclude: [{
                type: Input,
                args: ['seFeDataExclude']
            }], yearRange: [{
                type: Input,
                args: ['seFeDataYearRange']
            }], id: [{
                type: Input,
                args: ['seFeDataTableId']
            }], daysList: [{
                type: ViewChildren,
                args: [SeFeDatepickerCalendarDayComponent]
            }], manage: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

function invalidDate(date) {
    const luxDate = DateTime.fromFormat(date, DATE_FORMAT);
    return !luxDate.isValid;
}
function dateValidation(control) {
    if (!control.value)
        return null;
    return invalidDate(control.value) ? { dateInvalid: true } : null;
}

class SeFeDatepickerCalendarHeaderComponent {
    constructor(service) {
        this.service = service;
        this.years = [];
        this.months = this.monthsOptions;
        this.selectedMonth = this.service.date.month;
        this.selectedYear = this.service.date.year;
        this.subscription = this.service.calendarDays$.subscribe(() => {
            this.selectedMonth = this.service.date.month;
            this.selectedMonthLong = this.service.date.monthLong;
            this.selectedYear = this.service.date.year;
        });
    }
    ngOnChanges(changes) {
        changes.yearRange ? this.getYears(this.yearRange) : null;
    }
    changeMonth(index) {
        this.service.date = this.service.date.set({ month: index });
        this.selectedMonth = this.service.date.month;
        this.service.createCalendar();
    }
    changeYear(event) {
        this.service.date = this.service.date.set({ year: event });
        this.selectedYear = this.service.date.year;
        this.service.createCalendar();
    }
    nextMonth() {
        this.service.date = this.service.date.plus({ month: 1 });
        this.service.createCalendar();
    }
    previousMonth() {
        this.service.date = this.service.date.minus({ month: 1 });
        this.service.createCalendar();
    }
    getYears(years) {
        const { sort } = years;
        const currentYear = new Date().getFullYear();
        const maxYear = currentYear + (years.future || 0);
        const minYear = currentYear - (years.past || 0);
        for (let y = minYear; y <= maxYear; y++) {
            this.years.push(y);
        }
        if (sort === 'desc') {
            this.years.reverse();
        }
        return this.years;
    }
    get monthsOptions() {
        const monthNames = Info.months();
        return monthNames.map((month, i) => {
            return { name: `${i + 1} - ${month}` };
        });
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
SeFeDatepickerCalendarHeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarHeaderComponent, deps: [{ token: SeFeDatepickerService }], target: i0.ɵɵFactoryTarget.Component });
SeFeDatepickerCalendarHeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeDatepickerCalendarHeaderComponent, selector: "se-fe-datepicker-calendar-header", inputs: { yearRange: ["seFeDataYearRange", "yearRange"], id: ["seFeDataHeaderId", "id"] }, usesOnChanges: true, ngImport: i0, template: "<div class=\"se-fe-datepicker__header\">\n  <div class=\"se-fe-datepicker__select-month\">\n    <select aria-label=\"Month\" name=\"month\" [(ngModel)]=\"selectedMonth\" (ngModelChange)=\"changeMonth($event)\">\n      <option *ngFor=\"let month of months; let i = index\" [ngValue]=\"i + 1\">{{ month.name }}</option>\n    </select>\n  </div>\n \n  <div class=\"se-fe-datepicker__select-year\">\n    <select aria-label=\"Year\" name=\"year\" [(ngModel)]=\"selectedYear\" (ngModelChange)=\"changeYear($event)\">\n      <option *ngFor=\"let year of years\" [value]=\"year\">{{ year }}</option>\n    </select>\n  </div>\n  \n  <div class=\"se-fe-datepicker__month-buttons\">\n    <button type=\"button\" (click)=\"previousMonth()\" aria-label=\"Previous Month\"><span class=\"se-fe-datepicker__prev-arrow\"></span></button>\n    <button type=\"button\" (click)=\"nextMonth()\" aria-label=\"Next Month\"><span class=\"se-fe-datepicker__next-arrow\"></span></button>\n  </div>\n</div>\n", styles: ["@font-face{font-family:\"Forza\";font-style:normal;font-weight:200;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/Forza-Thin.woff) format(\"woff\")}@font-face{font-family:\"Forza\";font-style:normal;font-weight:400;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/Forza-Light.woff) format(\"woff\")}@font-face{font-family:\"Forza\";font-style:normal;font-weight:600;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/Forza-Medium.woff) format(\"woff\")}@font-face{font-family:\"Forza\";font-style:normal;font-weight:700;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/Forza-Bold.woff) format(\"woff\")}@font-face{font-family:\"Gotham\";font-style:normal;font-weight:200;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/Gotham-Light.woff) format(\"woff\")}@font-face{font-family:\"Gotham\";font-style:italic;font-weight:200;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/Gotham-LightItalic.woff) format(\"woff\")}@font-face{font-family:\"Gotham\";font-style:normal;font-weight:400;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/Gotham-Book.woff) format(\"woff\")}@font-face{font-family:\"Gotham\";font-style:italic;font-weight:400;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/Gotham-BookItalic.woff) format(\"woff\")}@font-face{font-family:\"Gotham\";font-style:normal;font-weight:600;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/Gotham-Medium.woff) format(\"woff\")}@font-face{font-family:\"Gotham\";font-style:italic;font-weight:600;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/Gotham-MediumItalic.woff) format(\"woff\")}@font-face{font-family:\"Gotham\";font-style:normal;font-weight:700;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/Gotham-Bold.woff) format(\"woff\")}@font-face{font-family:\"Gotham\";font-style:italic;font-weight:700;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/Gotham-BoldItalic.woff) format(\"woff\")}@font-face{font-family:\"Open Sans\";font-style:normal;font-weight:200;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/OpenSans-Light.woff) format(\"woff\")}@font-face{font-family:\"Open Sans\";font-style:italic;font-weight:200;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/OpenSans-LightItalic.woff) format(\"woff\")}@font-face{font-family:\"Open Sans\";font-style:normal;font-weight:400;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/OpenSans-Regular.woff) format(\"woff\")}@font-face{font-family:\"Open Sans\";font-style:italic;font-weight:400;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/OpenSans-Italic.woff) format(\"woff\")}@font-face{font-family:\"Open Sans\";font-style:normal;font-weight:600;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/OpenSans-SemiBold.woff) format(\"woff\")}@font-face{font-family:\"Open Sans\";font-style:italic;font-weight:600;src:url(https://sportngin-snap-production.s3.amazonaws.com/ui_themes/assets/latest/fonts/OpenSans-SemiboldItalic.woff) format(\"woff\")}.se-fe-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0}.se-fe-sr-only-focusable:focus{position:relative;width:auto;height:auto;clip:initial;margin:inherit;padding:inherit;border:initial}.se-fe-datepicker__prev-arrow:after,.se-fe-datepicker__next-arrow:after,.se-fe-datepicker__header .se-fe-datepicker__select-year:after,.se-fe-datepicker__header .se-fe-datepicker__select-month:after{width:calc(var(--se-rem, 1rem) * .33);height:calc(var(--se-rem, 1rem) * .33)}.se-fe-datepicker__header .se-fe-datepicker__select-year:after,.se-fe-datepicker__header .se-fe-datepicker__select-month:after{content:\"\";border:1px solid #23282b;margin:0;display:inline-block;border-left:none;border-bottom:none;position:absolute;right:12px;top:calc(25% + var(--se-rem, 1rem) * .165);transform:rotate(135deg)}.se-fe-datepicker__header .se-fe-datepicker__select-year select,.se-fe-datepicker__header .se-fe-datepicker__select-month select{color:#23282b;padding:4px 12px 4px 8px;border:1px solid #c1cace;border-radius:4px;-webkit-appearance:none;appearance:none;text-align:left;height:28px}.se-fe-datepicker__header .se-fe-datepicker__select-year select:focus,.se-fe-datepicker__header .se-fe-datepicker__select-month select:focus{outline:0;box-shadow:0 0 0 calc(0px + 2px) #fff,0 0 0 calc(0px + 4px) #00668f,0 0 0 calc(0px + 6px) #fff}.se-fe-datepicker__header{border-bottom:1px solid #c1cace;display:flex;flex-direction:row;padding:12px}.se-fe-datepicker__header button{border:1px solid #c1cace;background:none;padding:5px 10px}.se-fe-datepicker__header button:first-of-type{border-bottom-left-radius:4px;border-top-left-radius:4px;border-right:none}.se-fe-datepicker__header button:last-of-type{border-bottom-right-radius:4px;border-top-right-radius:4px}.se-fe-datepicker__header button:focus{outline:0;z-index:0;border-radius:4px;border:1px solid #c1cace;box-shadow:0 0 0 calc(1px + 2px) #fff,0 0 0 calc(1px + 4px) #00668f,0 0 0 calc(1px + 6px) #fff}.se-fe-datepicker__header .se-fe-datepicker__select-month{position:relative;margin-right:8px}.se-fe-datepicker__header .se-fe-datepicker__select-month select{width:121px}.se-fe-datepicker__header .se-fe-datepicker__select-year{position:relative}.se-fe-datepicker__header .se-fe-datepicker__select-year select{width:75px}.se-fe-datepicker__prev-arrow:after,.se-fe-datepicker__next-arrow:after{content:\"\";border:1px solid #23282b;margin:0;display:inline-block;border-left:none;border-bottom:none;vertical-align:middle}.se-fe-datepicker__prev-arrow:after{transform:rotate(225deg);margin-left:calc(var(--se-rem, 1rem) * .3166)}.se-fe-datepicker__next-arrow:after{transform:rotate(45deg);margin-right:calc(var(--se-rem, 1rem) * .3166)}.se-fe-datepicker__month-buttons{display:flex;flex:auto;justify-content:flex-end}\n"], directives: [{ type: i2.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { type: i6.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i2.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { type: i2.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-datepicker-calendar-header',
                    templateUrl: './se-fe-datepicker-calendar-header.component.html',
                    styleUrls: ['./se-fe-datepicker-calendar-header.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: SeFeDatepickerService }]; }, propDecorators: { yearRange: [{
                type: Input,
                args: ['seFeDataYearRange']
            }], id: [{
                type: Input,
                args: ['seFeDataHeaderId']
            }] } });

class SeFeDatepickerCalendarFooterComponent {
    constructor() {
        this.seFeClick = new EventEmitter();
    }
    close() {
        this.seFeClick.emit('close');
    }
    setDate() {
        this.seFeClick.emit('setDate');
    }
}
SeFeDatepickerCalendarFooterComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SeFeDatepickerCalendarFooterComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeDatepickerCalendarFooterComponent, selector: "se-fe-datepicker-calendar-footer", outputs: { seFeClick: "seFeClick" }, ngImport: i0, template: "<se-fe-button [seFeDataEmphasis]=\"'low'\" (click)=\"close()\">Cancel</se-fe-button>\n<se-fe-button [seFeDataEmphasis]=\"'high'\" (click)=\"setDate()\">Ok</se-fe-button>\n", styles: [".se-fe-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0}.se-fe-sr-only-focusable:focus{position:relative;width:auto;height:auto;clip:initial;margin:inherit;padding:inherit;border:initial}:host{border-top:1px solid #c1cace;padding:16px 12px;display:flex;justify-content:flex-end}:host se-fe-button{margin-right:12px}\n"], components: [{ type: i1.SeFeButtonComponent, selector: "se-fe-button", inputs: ["seFeDataIsDisabled", "seFeDataEmphasis", "seFeDataIsLoading", "seFeDataFullWidth", "seFeDataType", "seFeDataLoadingLabel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerCalendarFooterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-datepicker-calendar-footer',
                    templateUrl: './se-fe-datepicker-calendar-footer.component.html',
                    styleUrls: ['./se-fe-datepicker-calendar-footer.component.scss'],
                }]
        }], propDecorators: { seFeClick: [{
                type: Output
            }] } });

class SeFeDatepickerComponent {
    constructor(renderer, service, injector) {
        this.renderer = renderer;
        this.service = service;
        this.injector = injector;
        this.baseClass = true;
        this.isOpened = false;
        this.icon = false;
        this.isComfy = false;
        this.suffix = '';
        this.errorMessages = {};
        this.exclude = [];
        this.subscription = [];
        // set IDs
        this.labelId = `se-fe-datepicker-label-${SeFeDatepickerComponent.counter}-${Date.now()}`;
        this.id = `se-fe-datepicker-${SeFeDatepickerComponent.counter}-${Date.now()}`;
        this.errorId = `se-fe-datepicker-error-${SeFeDatepickerComponent.counter}-${Date.now()}`;
        this.helpId = `se-fe-datepicker-help-${SeFeDatepickerComponent.counter}-${Date.now()}`;
        SeFeDatepickerComponent.counter++;
        this.renderer.listen('window', 'click', (e) => {
            var _a, _b;
            if (!e.composedPath().includes((_a = this.calendar) === null || _a === void 0 ? void 0 : _a.nativeElement) && !e.composedPath().includes((_b = this.calendarInput) === null || _b === void 0 ? void 0 : _b.nativeElement)) {
                this.isOpened = false;
                this.updateServiceDate();
            }
        });
        const sub = this.service.selectedDate$
            .subscribe(date => {
            this.onChange(date.toFormat(DATE_FORMAT));
            this.onTouched(date.toFormat(DATE_FORMAT));
            this.updateServiceDate();
            this.isOpened = false;
            this.calendarButton.nativeElement.focus();
        });
        this.subscription.push(sub);
    }
    onKeydown(event) {
        if (event.code === 'Escape') {
            if (this.isOpened)
                this.toggleCalendar();
        }
    }
    get errors() {
        if (!this._control.touched && !this._control.dirty)
            return [];
        const parentErrors = this.parentControl.errors || {};
        return Object.keys(parentErrors);
    }
    ngOnInit() {
        this.parentControl = this.parentFormControl;
        this.svgPath = SeFeCalendarSvgPath[this.iconPath];
        this._control = this.injector.get(NgControl);
        if (this._control !== null) {
            this._control.valueAccessor = this;
        }
        setTimeout(() => {
            // Handle form reset
            const sub = this._control.valueChanges.subscribe(val => {
                val === null ? this.setValue('') : '';
            });
            this.subscription.push(sub);
            // Handle inital data
            const newDate = DateTime.fromJSDate(new Date(this._control.value.replace(/-/g, '/')));
            if (newDate.isValid) {
                this.setValue(newDate.toFormat(DATE_FORMAT));
                this.service.date = newDate;
            }
            this.resetFormValidators();
        });
    }
    get calendarIconLabel() {
        if (this._control.value) {
            const inputDate = DateTime.fromJSDate(new Date(this._control.value.replace(/-/g, '/')));
            const weekday = inputDate.weekdayLong;
            const dateFormat = inputDate.toFormat('LLLL d yyyy');
            const str = `Change date, ${weekday} ${dateFormat}`;
            return `${str.slice(0, -5)},${str.slice(-5)}`;
        }
        return 'Choose date';
    }
    handleFooterAction(evt) {
        if (evt === 'close')
            this.toggleCalendar();
        if (evt === 'setDate')
            this.service.selectedDate(this.service.date, this.exclude);
    }
    get invalid() {
        return this._control ? this._control.invalid : false;
    }
    get showErrors() {
        if (!this._control)
            return false;
        const { dirty, touched } = this._control;
        return this.invalid ? dirty && touched : false;
    }
    toggleCalendar() {
        this.isOpened = !this.isOpened;
        if (this.isOpened) {
            this.service.createCalendar();
            this.updateServiceDate();
        }
        else {
            this.updateServiceDate();
            setTimeout(() => {
                this.calendarButton.nativeElement.focus();
            });
        }
    }
    get ariaDesc() {
        return this.showErrors && this.errors.length ? this.errorId : this.helpId;
    }
    change($event) {
        this.onChange($event.currentTarget.value);
    }
    touched($event) {
        this.onTouched($event.currentTarget.touched);
    }
    writeValue(value) {
        this._value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        if (isDisabled)
            this.parentControl.disable();
        else
            this.parentControl.enable();
    }
    ngOnDestroy() {
        this.subscription.forEach(sub => sub.unsubscribe());
    }
    reformatDate($event) {
        if (this._control.value) {
            const newDate = DateTime.fromJSDate(new Date($event.currentTarget.value));
            if (newDate.isValid) {
                this.setValue(newDate.toFormat(DATE_FORMAT));
            }
        }
    }
    updateServiceDate() {
        const inputDate = DateTime.fromJSDate(new Date(this._control.value));
        const date = inputDate.isValid ? inputDate : DateTime.now();
        this.service.date = date;
        this.service.inputDate = date;
    }
    setValue(value) {
        this._value = value;
        this.onChange(this._value);
        this.onTouched(this._value);
    }
    get iconPath() {
        return this.icon ? 'range' : 'today';
    }
    resetFormValidators() {
        this._control.control.setValidators(this._control.control.validator ?
            [this._control.control.validator, dateExcludedValidator(this.exclude), dateYearRangeValidator(this.yearRange), dateValidation] :
            [dateExcludedValidator(this.exclude), dateYearRangeValidator(this.yearRange), dateValidation]);
    }
    get parentFormControl() {
        const control = this.injector.get(NgControl);
        if (!control)
            return;
        // Test setup uses `this.control.control`. I couldn't figure out how to mock the parent relationship correctly.
        if (control.control)
            return control.control;
        // The formControlName API says .control should return the formControl, but is does not, so we look it up from the _parent.
        return control._parent.control.controls[control.name];
    }
}
SeFeDatepickerComponent.counter = 1;
SeFeDatepickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerComponent, deps: [{ token: i0.Renderer2 }, { token: SeFeDatepickerService }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component });
SeFeDatepickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeDatepickerComponent, selector: "se-fe-datepicker", inputs: { label: ["seFeDataLabel", "label"], helpText: ["seFeDataHelpText", "helpText"], icon: ["seFeDataDateRangeIcon", "icon"], isComfy: ["seFeDataComfy", "isComfy"], suffix: ["seFeDataSuffix", "suffix"], suffixSize: ["seFeDataSuffixSize", "suffixSize"], errorMessages: ["seFeDataMessages", "errorMessages"], exclude: ["seFeDataExcludeDates", "exclude"], yearRange: ["seFeDataYearRange", "yearRange"] }, host: { listeners: { "keydown": "onKeydown($event)" }, properties: { "class.se-fe-datepicker": "this.baseClass" } }, providers: [
        SeFeDatepickerService,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SeFeDatepickerComponent),
            multi: true,
        },
    ], viewQueries: [{ propertyName: "calendar", first: true, predicate: ["calendar"], descendants: true }, { propertyName: "calendarInput", first: true, predicate: ["calendarInput"], descendants: true }, { propertyName: "calendarButton", first: true, predicate: ["calendarButton"], descendants: true }, { propertyName: "calendarDays", first: true, predicate: SeFeDatepickerCalendarComponent, descendants: true }], ngImport: i0, template: "<div class=\"se-fe-datepicker__form\">\n  <div #calendarInput>\n    <label [attr.for]=\"labelId\" class=\"se-fe-datepicker__label\">{{ label }}</label>\n    <ng-template #controls>\n      <div\n        class=\"se-fe-datepicker__input-field\"\n        [ngClass]=\"{'se-fe-datepicker__input-field--comfy': isComfy}\">\n        <input\n          (blur)=\"touched($event)\"\n          (input)=change($event)\n          type=\"text\"\n          [attr.id]=\"labelId\"\n          placeholder=\"mm/dd/yyyy\"\n          class=\"se-fe-datepicker__input\"\n          [ngClass]=\"{ 'se-fe-datepicker__input--errors': showErrors }\"\n          (focusin)=\"isOpened = false\"\n          (focusout)=\"reformatDate($event)\"\n          value=\"{{ _control.value }}\"\n          [attr.aria-describedby]=\"ariaDesc\"/>\n        <button #calendarButton type=\"button\" (click)=\"toggleCalendar()\" [attr.aria-label]=\"calendarIconLabel\" class=\"se-fe-datepicker__button\">\n          <svg class=\"se-fe-datepicker__button-icon\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" height=\"24px\" viewBox=\"0 0 24 24\" width=\"24px\" preserveAspectRatio=\"xMidYMid meet\" aria-hidden=\"true\">\n            <path d=\"M0 0h24v24H0V0z\" fill=\"none\"/>\n            <path stroke-width=\"0\" [attr.d]=\"this.svgPath\"></path>\n          </svg>\n        </button>\n      </div>\n    </ng-template>\n    <se-fe-form-field-suffix\n      *ngIf=\"suffix\"\n      [seFeDataSuffix]=\"suffix\"\n      [seFeDataSize]=\"suffixSize\">\n      <ng-container *ngTemplateOutlet=\"controls\"></ng-container>\n    </se-fe-form-field-suffix>\n    <ng-container *ngIf=\"!suffix\">\n      <ng-container *ngTemplateOutlet=\"controls\"></ng-container>\n    </ng-container>\n\n    <div *ngIf=\"showErrors && errors.length\" [attr.id]=\"errorId\">\n      <div *ngFor=\"let message of errors\" class=\"se-fe-datepicker--error-message\">\n        {{ errorMessages[message] }}\n      </div>\n    </div>\n    <div *ngIf=\"!showErrors || !errors.length\" class=\"se-fe-datepicker__help\" [attr.id]='helpId'>\n      {{ helpText }}\n    </div>\n  </div>\n</div>\n\n<div #calendar *ngIf=\"isOpened\" role=\"dialog\" aria-modal=\"true\" aria-label=\"Choose Date\" aria-describedby=\"sr-instructions\" class=\"se-fe-datepicker__calendar\" cdkTrapFocus>\n  <se-fe-datepicker-calendar-header [seFeDataHeaderId]=\"id\" [seFeDataYearRange]=\"yearRange\"></se-fe-datepicker-calendar-header>\n  <se-fe-datepicker-calendar [seFeDataTableId]=\"id\" [seFeDataExclude]=\"exclude\" [seFeDataYearRange]=\"yearRange\"></se-fe-datepicker-calendar>\n  <se-fe-datepicker-calendar-footer (seFeClick)=\"handleFooterAction($event)\"></se-fe-datepicker-calendar-footer>\n  <div class=\"se-fe-sr-only\" id=\"sr-instructions\" aria-live=\"polite\">Cursor keys can navigate dates</div>\n</div>\n", styles: [":host{display:block;font:normal 400 calc(var(--se-rem, 1rem) * 1)/1.4286 \"Open Sans\",sans-serif;box-sizing:border-box}.se-fe-datepicker{display:flex}.se-fe-datepicker__label{color:#68767f;padding-bottom:4px;display:block}.se-fe-datepicker__input-field{position:relative}.se-fe-datepicker__input{font:normal 400 calc(var(--se-rem, 1rem) * 1)/1.4286 \"Open Sans\",sans-serif;font-size:calc(var(--se-rem, 1rem) * 1);line-height:18px;color:#23282b;box-sizing:border-box;padding:8px 12px;height:38px;width:100%;border-radius:4px;border:1px solid #c1cace}.se-fe-datepicker__input:focus{outline:0;box-shadow:0 0 0 calc(1px + 2px) #fff,0 0 0 calc(1px + 4px) #00668f,0 0 0 calc(1px + 6px) #fff}.se-fe-datepicker__input::placeholder{color:#c1cace}.se-fe-datepicker__input--active{border:2px solid #00668f}.se-fe-datepicker__input--errors{border-color:#b42846}.se-fe-datepicker__help{font-size:calc(var(--se-rem, 1rem) * .8571428571);color:#c1cace;margin-top:4px}.se-fe-datepicker__button{height:38px;background:none;border:none;position:absolute;padding:0;right:7px;cursor:pointer}.se-fe-datepicker__button:focus{outline:0}.se-fe-datepicker__button:focus .se-fe-datepicker__button-icon{border-radius:4px;outline:0;box-shadow:0 0 0 calc(0px + 2px) #fff,0 0 0 calc(0px + 4px) #00668f,0 0 0 calc(0px + 6px) #fff}.se-fe-datepicker__button-icon{vertical-align:middle}.se-fe-datepicker__calendar{position:absolute;z-index:50;margin-top:-19px;background:#fff;flex:1;display:flex;flex-direction:column;width:328px;justify-content:center;text-align:center;border-radius:2px;box-shadow:0 0 2px #0000001f,0 2px 2px #0000003d}.se-fe-datepicker__title{margin:8px 0}.se-fe-datepicker--error-message{color:#b42846;font-size:calc(var(--se-rem, 1rem) * .8571428571);margin-top:4px}.se-fe-datepicker__input-field--comfy .se-fe-datepicker__input{height:48px;padding:14px 21px}.se-fe-datepicker__input-field--comfy .se-fe-datepicker__button{height:48px;right:21px}\n"], components: [{ type: i2$1.SeFeFormFieldSuffixComponent, selector: "se-fe-form-field-suffix", inputs: ["seFeDataSuffix", "seFeDataSize"] }, { type: SeFeDatepickerCalendarHeaderComponent, selector: "se-fe-datepicker-calendar-header", inputs: ["seFeDataYearRange", "seFeDataHeaderId"] }, { type: SeFeDatepickerCalendarComponent, selector: "se-fe-datepicker-calendar", inputs: ["seFeDataExclude", "seFeDataYearRange", "seFeDataTableId"] }, { type: SeFeDatepickerCalendarFooterComponent, selector: "se-fe-datepicker-calendar-footer", outputs: ["seFeClick"] }], directives: [{ type: i6.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i6.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i6.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }, { type: i6.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i7.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-datepicker',
                    templateUrl: './se-fe-datepicker.component.html',
                    styleUrls: ['./se-fe-datepicker.component.scss'],
                    providers: [
                        SeFeDatepickerService,
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SeFeDatepickerComponent),
                            multi: true,
                        },
                    ],
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: SeFeDatepickerService }, { type: i0.Injector }]; }, propDecorators: { baseClass: [{
                type: HostBinding,
                args: ['class.se-fe-datepicker']
            }], label: [{
                type: Input,
                args: ['seFeDataLabel']
            }], helpText: [{
                type: Input,
                args: ['seFeDataHelpText']
            }], icon: [{
                type: Input,
                args: ['seFeDataDateRangeIcon']
            }], isComfy: [{
                type: Input,
                args: ['seFeDataComfy']
            }], suffix: [{
                type: Input,
                args: ['seFeDataSuffix']
            }], suffixSize: [{
                type: Input,
                args: ['seFeDataSuffixSize']
            }], errorMessages: [{
                type: Input,
                args: ['seFeDataMessages']
            }], exclude: [{
                type: Input,
                args: ['seFeDataExcludeDates']
            }], yearRange: [{
                type: Input,
                args: ['seFeDataYearRange']
            }], calendar: [{
                type: ViewChild,
                args: ['calendar']
            }], calendarInput: [{
                type: ViewChild,
                args: ['calendarInput']
            }], calendarButton: [{
                type: ViewChild,
                args: ['calendarButton']
            }], calendarDays: [{
                type: ViewChild,
                args: [SeFeDatepickerCalendarComponent]
            }], onKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class SeFeDatepickerModule {
}
SeFeDatepickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SeFeDatepickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerModule, declarations: [SeFeDatepickerComponent,
        SeFeDatepickerCalendarHeaderComponent,
        SeFeDatepickerCalendarFooterComponent,
        SeFeDatepickerCalendarComponent,
        SeFeDatepickerCalendarDayComponent], imports: [CommonModule,
        FormsModule,
        SeFeButtonModule,
        SeFeFormFieldSuffixModule,
        A11yModule], exports: [SeFeDatepickerComponent, FormsModule] });
SeFeDatepickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerModule, imports: [[
            CommonModule,
            FormsModule,
            SeFeButtonModule,
            SeFeFormFieldSuffixModule,
            A11yModule,
        ], FormsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeDatepickerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SeFeDatepickerComponent,
                        SeFeDatepickerCalendarHeaderComponent,
                        SeFeDatepickerCalendarFooterComponent,
                        SeFeDatepickerCalendarComponent,
                        SeFeDatepickerCalendarDayComponent,
                    ],
                    imports: [
                        CommonModule,
                        FormsModule,
                        SeFeButtonModule,
                        SeFeFormFieldSuffixModule,
                        A11yModule,
                    ],
                    exports: [SeFeDatepickerComponent, FormsModule],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DATE_FORMAT, SeFeCalendarSvgPath, SeFeDatepickerComponent, SeFeDatepickerModule };
